import {createAsyncThunk} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {systemConfirm} from '@web/actions/application'
import {DELIVERY_MEMO_ERROR_MESSAGE, DELIVERY_VALIDATION_RESULT_TYPE} from '@web/core/constants'
import {
    validateAddress,
    validateVirtualPhoneNumberAvailable,
    checkMemoIncludesInvisibleExceptSpace,
    checkMemoLength,
} from '@web/helpers/policies'
import {
    BaseDeliveryAddress,
    DELIVERY_MANAGER_TYPE,
    deliveryAddressByHashSelector,
    deliveryManagerSelector,
    type DeliveryManagerState,
    personalCustomsCodeManagerSelector,
} from '@web/slices/deliveryManager/slice'

export const confirmDeliveryInfo = createAsyncThunk<
    BaseDeliveryAddress,
    void,
    {state: {deliveryManager: DeliveryManagerState}; rejectValue: {code?: string; message?: string}}
>('deliveryManager/confirmDeliveryInfo', async (_, {getState, dispatch, rejectWithValue}) => {
    const {
        orderInfo: {
            isOverseasDelivery,
            hasArrivalGuaranteeProduct,
            virtualPhoneNumber,
            deliveryMemo: {
                memoList,
                options: {
                    particularMemo: {used: isParticularMemoUsed},
                },
            },
        },
        options: {type},
    } = deliveryManagerSelector(getState())
    const deliveryAddress = deliveryAddressByHashSelector(getState())
    const {isValid: isPersonalCustomsCodeValid} = personalCustomsCodeManagerSelector(getState())

    if (!isPersonalCustomsCodeValid) {
        return rejectWithValue({
            code: 'PERSONAL_CUSTOMS_CODE_INPUT_FAIL',
            message: 'P로 시작하는 12자리 숫자로 입력해주세요.',
        })
    }

    if (!deliveryAddress) {
        return rejectWithValue({message: '배송지를 선택해주세요'})
    }

    // 주소 validation
    const {type: validationResultType, message} = validateAddress({
        addressInfo: deliveryAddress,
        isOverseasDelivery,
        hasArrivalGuaranteeProduct,
        ignoreField: type === DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY ? ['detailAddress'] : [],
    })

    const eqValidationResultType = eq(validationResultType)

    // INVALID일 경우 에러메시지 띄우고 reject
    if (eqValidationResultType(DELIVERY_VALIDATION_RESULT_TYPE.INVALID)) {
        return rejectWithValue({message})
    }
    // WARNING은 confirm 확인 시 진행. 취소 시 따로 얼럿메시지 띄우지 않고 액션만 리젝.
    if (eqValidationResultType(DELIVERY_VALIDATION_RESULT_TYPE.WARNING)) {
        const isConfirmed = await new Promise<boolean>((resolve) => {
            dispatch(
                systemConfirm({
                    type: 'CONFIRM',
                    message,
                    callback: (confirmed: boolean) => {
                        resolve(confirmed)
                    },
                }),
            )
        })
        if (!isConfirmed) {
            return rejectWithValue({})
        }
    }

    // 연락처 1, 2를 받았을 때 해당번호를 안심번호로 사용할 수 있는지 여부 검증.
    const {type: validationVirtualPhoneNumberResultType, message: virtualPhoneNumberUseErrorMsg} =
        validateVirtualPhoneNumberAvailable({telNo1: deliveryAddress.telNo1, telNo2: deliveryAddress.telNo2})
    const eqValidationVirtualPhoneNumber = eq(validationVirtualPhoneNumberResultType)

    if (virtualPhoneNumber.used && eqValidationVirtualPhoneNumber(DELIVERY_VALIDATION_RESULT_TYPE.INVALID)) {
        return rejectWithValue({message: virtualPhoneNumberUseErrorMsg})
    }

    // 배송메모 validation
    const singleMemoList = memoList[0] ? [memoList[0]] : []
    const memos = isParticularMemoUsed ? memoList : singleMemoList
    if (memos.some((memo) => checkMemoIncludesInvisibleExceptSpace(memo.value))) {
        return rejectWithValue({message: DELIVERY_MEMO_ERROR_MESSAGE.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL})
    }
    if (memos.some((memo) => checkMemoLength(memo.value))) {
        return rejectWithValue({message: DELIVERY_MEMO_ERROR_MESSAGE.MEMO_MAX_FAIL})
    }

    return deliveryAddress
})
